import { theme } from "@mindlercare/ui-web";
import { differenceInCalendarDays } from "date-fns";
import * as R from "remeda";
import { QuestionnaireRequest } from "core/types/index";

export type QuestionnaireRequestsStatuses =
  | "NONE_SENT"
  | "PENDING_RESPONSE"
  | "NEW_RESPONSE"
  | "LAST_RESPONSE";

type QuestionnaireStatusColors = Extract<
  keyof typeof theme.COLORS,
  "BLUE_DARK" | "SECONDARY_DARK" | "SUCCESS"
>;

export type QuestionnaireStatusResult =
  | {
      status: Exclude<
        QuestionnaireRequestsStatuses,
        "LAST_RESPONSE" | "NONE_SENT"
      >;
      count: number;
      color: QuestionnaireStatusColors;
      daysSince: number;
    }
  | {
      status: Exclude<
        QuestionnaireRequestsStatuses,
        "PENDING_RESPONSE" | "NEW_RESPONSE"
      >;
      color: QuestionnaireStatusColors;
      daysSince: number;
    }
  | {
      status: Exclude<
        QuestionnaireRequestsStatuses,
        "PENDING_RESPONSE" | "NEW_RESPONSE" | "LAST_RESPONSE"
      >;
      color: QuestionnaireStatusColors;
    };

const isQuestionnaireRequestWithNewResponse = (
  request: QuestionnaireRequest,
): boolean => {
  if (request.completedAt && !request.psychologistSeenAt) {
    return true;
  }
  return false;
};

const isPendingQuestionnaireRequest = (
  request: QuestionnaireRequest,
): boolean => {
  if (!request.completedAt) {
    return true;
  }

  return false;
};

const getDaysSinceFrom = (dateString: string | undefined | null) => {
  if (!dateString) return 0;
  return differenceInCalendarDays(new Date(), new Date(dateString));
};

export const getQuestionnaireRequestsStatus = (
  questionnaireRequests: QuestionnaireRequest[],
): QuestionnaireStatusResult => {
  const sortedRequests = R.pipe(
    questionnaireRequests,
    R.sortBy((r) => {
      if (!r.completedAt) return 0;
      return new Date(r.completedAt).getTime();
    }),
    R.reverse(),
  );

  if (questionnaireRequests.length === 0) {
    return {
      status: "NONE_SENT",
      color: "SECONDARY_DARK",
    };
  }

  const newResponses = sortedRequests.filter(
    isQuestionnaireRequestWithNewResponse,
  );

  if (newResponses.length > 0) {
    return {
      status: "NEW_RESPONSE",
      count: newResponses.length,
      color: "BLUE_DARK",
      daysSince: getDaysSinceFrom(newResponses[0]?.completedAt),
    };
  }

  const pendingResponses = sortedRequests.filter(isPendingQuestionnaireRequest);

  if (pendingResponses.length > 0) {
    return {
      status: "PENDING_RESPONSE",
      count: pendingResponses.length,
      color: "SECONDARY_DARK",
      daysSince: getDaysSinceFrom(pendingResponses[0]?.requestedAt),
    };
  }

  const daysSinceLatestAnswer = getDaysSinceFrom(
    sortedRequests[0]?.completedAt,
  );

  if (daysSinceLatestAnswer > 6) {
    return {
      status: "LAST_RESPONSE",
      color: "SECONDARY_DARK",
      daysSince: daysSinceLatestAnswer,
    };
  }

  return {
    status: "LAST_RESPONSE",
    color: "SUCCESS",
    daysSince: daysSinceLatestAnswer,
  };
};
